// frontend/src/components/InvitacionesManager.jsx
import React, { useState, useEffect } from 'react';
import contentService from '../services/contentService';
import Loading from './Loading';
import './InvitacionesManager.css';

const InvitacionesManager = () => {
  const [invitaciones, setInvitaciones] = useState([]);
  const [tiposUsuario, setTiposUsuario] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [procesando, setProcesando] = useState(false);

  const [nuevaInvitacion, setNuevaInvitacion] = useState({
    email: '',
    tipo_usuario_id: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [invitacionesRes, tiposRes] = await Promise.all([
        contentService.listarInvitaciones(),
        contentService.getTiposUsuario()
      ]);

      // 🔧 MANEJAR DIFERENTES FORMATOS DE RESPUESTA
      // Invitaciones puede venir como:
      // - { data: { invitaciones: [...] } }
      // - { data: [...] }
      const invitacionesData = invitacionesRes.data?.invitaciones ||
                              invitacionesRes.data?.data?.invitaciones ||
                              invitacionesRes.data ||
                              [];

      // Tipos puede venir como:
      // - { data: { data: [...] } }
      // - { data: [...] }
      const tiposData = tiposRes.data?.data ||
                       tiposRes.data ||
                       [];

      console.log('📊 Invitaciones cargadas:', invitacionesData);
      console.log('👥 Tipos de usuario cargados:', tiposData);

      setInvitaciones(Array.isArray(invitacionesData) ? invitacionesData : []);
      setTiposUsuario(Array.isArray(tiposData) ? tiposData : []);

    } catch (err) {
      console.error('❌ Error cargando datos:', err);
      console.error('Response:', err.response);
      setError(err.response?.data?.error || 'Error al cargar las invitaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setProcesando(true);

    try {
      await contentService.crearInvitacion(nuevaInvitacion);
      setSuccess('¡Invitación enviada con éxito!');
      setNuevaInvitacion({ email: '', tipo_usuario_id: '' });
      setShowModal(false);
      await cargarDatos();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creando invitación:', err);
      setError(err.response?.data?.error || 'Error al crear la invitación');
    } finally {
      setProcesando(false);
    }
  };

  const handleReenviar = async (invitacionId) => {
    if (!window.confirm('¿Deseas reenviar esta invitación?')) return;

    try {
      setProcesando(true);
      await contentService.reenviarInvitacion(invitacionId);
      setSuccess('Invitación reenviada con éxito');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error reenviando invitación:', err);
      setError(err.response?.data?.error || 'Error al reenviar la invitación');
    } finally {
      setProcesando(false);
    }
  };

  const handleCancelar = async (invitacionId) => {
    if (!window.confirm('¿Estás seguro de cancelar esta invitación?')) return;

    try {
      setProcesando(true);
      await contentService.cancelarInvitacion(invitacionId);
      setSuccess('Invitación cancelada exitosamente');
      await cargarDatos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error cancelando invitación:', err);
      setError(err.response?.data?.error || 'Error al cancelar la invitación');
    } finally {
      setProcesando(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { class: 'badge-warning', icon: '⏳', text: 'Pendiente' },
      'aceptada': { class: 'badge-success', icon: '✅', text: 'Aceptada' },
      'expirada': { class: 'badge-danger', icon: '⏰', text: 'Expirada' },
      'cancelada': { class: 'badge-secondary', icon: '❌', text: 'Cancelada' }
    };

    const badge = badges[estado] || badges.pendiente;
    return (
      <span className={`invitacion-badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Loading fullScreen={false} />;
  }

  return (
    <div className="invitaciones-manager">
      {/* Header */}
      <div className="invitaciones-header">
        <div>
          <h1 className="invitaciones-title">🎫 Gestión de Invitaciones</h1>
          <p className="invitaciones-subtitle">
            Administra las invitaciones a tu cuenta
          </p>
        </div>
        <button
          className="btn-nueva-invitacion"
          onClick={() => setShowModal(true)}
          disabled={procesando}
        >
          + Nueva Invitación
        </button>
      </div>

      {/* Alertas */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Estadísticas */}
      <div className="invitaciones-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{invitaciones.length}</div>
            <div className="stat-label">Total</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-value">
              {invitaciones.filter(i => i.estado === 'pendiente').length}
            </div>
            <div className="stat-label">Pendientes</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">
              {invitaciones.filter(i => i.estado === 'aceptada').length}
            </div>
            <div className="stat-label">Aceptadas</div>
          </div>
        </div>
      </div>

      {/* Lista de invitaciones */}
      <div className="invitaciones-list">
        {invitaciones.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No hay invitaciones</h3>
            <p>Crea tu primera invitación para invitar a alguien a tu cuenta</p>
            <button
              className="btn-primary"
              onClick={() => setShowModal(true)}
            >
              Crear primera invitación
            </button>
          </div>
        ) : (
          <div className="invitaciones-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Tipo de Usuario</th>
                  <th>Estado</th>
                  <th>Creado</th>
                  <th>Expira</th>
                  <th>Creado por</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invitaciones.map((invitacion) => (
                  <tr key={invitacion.id} className="invitacion-row">
                    <td>
                      <div className="email-cell">
                        <span className="email-icon">📧</span>
                        {invitacion.email}
                      </div>
                    </td>
                    <td>
                      <span className="tipo-badge">
                        {invitacion.tipo_usuario_nombre}
                      </span>
                    </td>
                    <td>{getEstadoBadge(invitacion.estado)}</td>
                    <td className="fecha-cell">
                      {formatearFecha(invitacion.fecha_creacion)}
                    </td>
                    <td className="fecha-cell">
                      {formatearFecha(invitacion.fecha_expiracion)}
                    </td>
                    <td>{invitacion.creado_por || '-'}</td>
                    <td>
                      <div className="acciones-cell">
                        {invitacion.estado === 'pendiente' && invitacion.esta_vigente && (
                          <>
                            <button
                              className="btn-action btn-reenviar"
                              onClick={() => handleReenviar(invitacion.id)}
                              disabled={procesando}
                              title="Reenviar invitación"
                            >
                              📨
                            </button>
                            <button
                              className="btn-action btn-cancelar"
                              onClick={() => handleCancelar(invitacion.id)}
                              disabled={procesando}
                              title="Cancelar invitación"
                            >
                              🚫
                            </button>
                          </>
                        )}
                        {invitacion.estado === 'aceptada' && (
                          <span className="text-muted">
                            Usado por: {invitacion.usado_por || 'N/A'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => !procesando && setShowModal(false)}>
          <div className="modal-content modal-l" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📬 Nueva Invitación</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
                disabled={procesando}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  value={nuevaInvitacion.email}
                  onChange={(e) => setNuevaInvitacion({
                    ...nuevaInvitacion,
                    email: e.target.value
                  })}
                  placeholder="usuario@ejemplo.com"
                  required
                  disabled={procesando}
                />
                <small className="form-help">
                  Se enviará un email con el código de invitación
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="tipo_usuario">
                  Tipo de Usuario <span className="required">*</span>
                </label>
                <select
                  id="tipo_usuario"
                  className="form-select"
                  value={nuevaInvitacion.tipo_usuario_id}
                  onChange={(e) => setNuevaInvitacion({
                    ...nuevaInvitacion,
                    tipo_usuario_id: e.target.value
                  })}
                  required
                  disabled={procesando}
                >
                  <option value="">Selecciona un tipo...</option>
                  {tiposUsuario.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </option>
                  ))}
                </select>
                <small className="form-help">
                  Define los permisos que tendrá el usuario invitado
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="estado-btn"
                  onClick={() => setShowModal(false)}
                  disabled={procesando}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={procesando}
                >
                  {procesando ? (
                    <>
                      <span className="spinner-small"></span>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Invitación'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitacionesManager;